import { useMemo, useState } from "react";
import { bool, object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { MAX_WORK_TYPE_LENGTH } from "@repo/utils";
import { generateWorkTypeId } from "@repo/structure";
import { DEFAULT_WORK_TYPE_COLOR, simplifyTextString } from "@utils";
import { useSearchableList, useEditorWebsocket } from "@hooks";
import { useEstimateEditorStore, useEstimateStructure } from "@tools";
import { SearchableListContent, SearchableListItem, TextClearInput, Tag } from "@components";
import classnames from "classnames";
import { DifferentSalesRateActionAlert } from "@tools/EstimateEditor/dialogs/DifferentSalesRateActionAlert";

export const WorkTypeSelect = observer(({
  workType,
  useExpandCell
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const editorStore = useEstimateEditorStore();
  const socket = useEditorWebsocket();
  
  const [ newWorkTypeData, setNewWorkTypeData ] = useState(null);
  
  const { workTypes } = editorStore;
  const { name, autoFocus, treePath } = workType;
  
  const newlyCreatedWorkType = useMemo(() => (
    autoFocus
  ), []);
  
  const handleRateAction = (newWT) => {
    updateWorkType(newWT, true)
    setNewWorkTypeData(null);
  }
  
  const updateWorkType = (newWT, setSystemId) => {
    if(!newlyCreatedWorkType)
      structure.historyManager.startGroup();
    
    const oldPath = treePath.join("/"), oldTimelinePath = "t" + oldPath, oldName = workType.name;
    
    workType.changeWorkType(
      newWT.id,
      setSystemId ? newWT.id : undefined,
      newWT.name,
      newWT.color,
      newWT.salesRate,
      newWT.costRate,
    );
    
    if(!newlyCreatedWorkType) {
      const newPath = workType.treePath.join("/"), newTimelinePath = "t" + newPath;
      
      socket?.requestCommentPathElementRename(oldPath, newWT.name);
      socket?.requestCommentPathElementRename(oldTimelinePath, newWT.name);
      socket?.requestCommentMove(oldPath, newPath);
      socket?.requestCommentMove(oldTimelinePath, newTimelinePath);
      structure.historyManager.addCommentNamePatch(oldPath, oldName, newWT.name);
      structure.historyManager.addCommentNamePatch(oldTimelinePath, oldName, newWT.name);
      structure.historyManager.addCommentPathPatch(oldPath, newPath);
      structure.historyManager.addCommentPathPatch(oldTimelinePath, newTimelinePath);
    }
    
    structure.historyManager.stopGroup();
  }
  
  const handleEmptyName = () => {
    if(newWorkTypeData)
      setNewWorkTypeData(null);
    
    if(workType.name)
      return;
    
    workType.removeSelf();
  }
  
  const handleNewWorkType = async ({ name: newName }) => {
    
    if(!newName && !workType.name) {
      handleEmptyName();
      return;
    }
    
    let newWT = workTypes.find(({ name, id }) => (
      simplifyTextString(name) === simplifyTextString(newName) && id !== workType.id
    ));
    
    if (newWT) {
      setNewWorkTypeData(newWT);
      return;
    }
    
    newWT = { id: generateWorkTypeId(), name: newName, backgroundColor: DEFAULT_WORK_TYPE_COLOR  };
    editorStore.addCustomWorkType(newWT.id, newWT.name, newWT.backgroundColor);
    updateWorkType(newWT);
  };
  
  const selectExistingWorkType = (newWT) => async () => {
    looseFocus();
    setNewWorkTypeData(newWT);
  };

  const getOptionDisabled = (value) => Boolean(
    structure.workTypes.find(
      ({ id, name }) => simplifyTextString(name) === simplifyTextString(value) && id !== workType.id
    )
  );

  const {
    containerProps,
    inputProps,
    hadAutofocus,
    hadInteraction,
    looseFocus
  } = useSearchableList({
    errorOnEmpty: true,
    defaultValue: name,
    autoFocus,
    onAutoFocus: workType.removeAutofocus,
    onChange: handleNewWorkType,
    onBlur: handleEmptyName,
    getOptionDisabled,
  });

  const availableWorkTypes = useMemo(() => {
    if(!hadInteraction)
      return workTypes;
    
    return workTypes.filter(({ name, hidden }) => {
      if(hidden)
        return;
      
      if(name === inputProps.value)
        return true;
      
      return simplifyTextString(name).indexOf(
        simplifyTextString(inputProps.value)
      ) >= 0;
    })
  }, [ workTypes, inputProps.value, hadInteraction ]);

  return (
    <>
      <TextClearInput
        {...inputProps}
        acceptOnClickAway
        confirmOnEnter
        placeholder={
          inputProps.focused
            ? t("views.editor.dialogs.work_types.focused")
            : t("common.work_type")
        }
        containerClass="name"
        className={classnames("-m-0-5", !useExpandCell && "ml-2")}
        maxLength={MAX_WORK_TYPE_LENGTH}
        preventClickAwayOnElement="#workTypeSelector"
        inputClassName="input-name"
        navigationKey="sm-name"
        noBlurFromEnterKey={hadAutofocus}
      />
      {
        Boolean(newWorkTypeData) &&
        <DifferentSalesRateActionAlert
          onAccept={handleRateAction}
          onCancel={handleEmptyName}
          newElementData={newWorkTypeData}
        />
      }
      <SearchableListContent {...containerProps} id="workTypeSelector">
        {availableWorkTypes.map((wT) => {
          const isUsed = structure.hasWorkType(wT.id);

          return (
            <SearchableListItem
              key={`wT_${wT.id}`}
              disabled={isUsed}
              onClick={selectExistingWorkType(wT)}
            >
              <Tag text={wT.name} color={wT.backgroundColor} />
            </SearchableListItem>
          );
        })}
      </SearchableListContent>
    </>
  );
});

WorkTypeSelect.propTypes = {
  workType: object.isRequired,
  useExpandCell: bool.isRequired,
};
